import type {
  GetPageResponse,
  ListWorkingState,
} from "@store/features/crm/pageV2Slice";
import { DataType } from "skedyul-db/browser";
import type {
  ColumnConfig,
  SpreadsheetColumnDataType,
} from "skedyul-ui/src/components/ui/DataView/Spreadsheet/util";
import {
  SpreadsheetConfig,
  SpreadsheetConfigSchema,
} from "skedyul-core/features/crm/features/pages";
import {
  createRelationshipCell,
  createSelectCell,
  createMultiSelectCell,
  createInputCell,
  createUrlCell,
  createDatePickerCell,
  createBooleanCell,
  createJsonCell,
} from "../../components/spreadsheet/index";
import { buildInstanceHrefFromPageData } from "../../helpers/pagePaths";
import {
  isEmailField,
  isPhoneField,
  isUrlField,
} from "../../components/spreadsheet/utils";
import type { Params } from "../../helpers";
import type {
  GenerateColumnsArgs,
  SpreadsheetColumn,
  SpreadsheetColumnsCacheEntry,
  RowData,
  ModelLite,
} from "./types";
import {
  resolveAccessorPath,
  resolveSpreadsheetColumnField,
  resolveSpreadsheetColumnModel,
} from "./columnResolve";

export {
  resolveAccessorPath,
  resolveSpreadsheetColumnField,
  resolveSpreadsheetColumnModel,
} from "./columnResolve";

type ColumnConfigWithSizing = ColumnConfig<RowData> & {
  maxWidth?: number;
  columnDataType?: SpreadsheetColumnDataType;
};

export const resolveSpreadsheetColumnDataType = (field: {
  relationshipLink?: { cardinality: string } | null;
  dataType?: string | null;
  definition?: {
    handle?: string;
    type?: string;
    options?: Array<{ value: string; label: string }>;
    limitChoices?: number | null;
    normalizations?: Array<{ handle: string }>;
  } | null;
}): SpreadsheetColumnDataType => {
  if (field.relationshipLink) return "relation";
  if (isUrlField(field)) return "url";
  if (field.definition?.options && field.definition.options.length > 0) {
    return field.definition.limitChoices === 1 ? "select" : "multi_select";
  }
  if (isPhoneField(field)) return "phone";
  if (isEmailField(field)) return "email";

  const resolvedType =
    (field.definition?.type as DataType | undefined) ??
    (field.dataType as DataType | undefined);

  switch (resolvedType) {
    case DataType.LONG_STRING:
      return "long_string";
    case DataType.NUMBER:
      return "number";
    case DataType.DATE:
      return "date";
    case DataType.DATE_TIME:
      return "date_time";
    case DataType.TIME:
      return "time";
    case DataType.BOOLEAN:
      return "boolean";
    case DataType.RELATION:
      return "relation";
    case DataType.STRING:
    case DataType.FILE:
    case DataType.IMAGE:
    case DataType.OBJECT:
    default:
      return "string";
  }
};

// Client-side sanitizer for spreadsheet configs to dedupe columns
export const sanitizeSpreadsheetConfig = (
  config: unknown,
): SpreadsheetConfig => {
  const parsed = SpreadsheetConfigSchema.safeParse(config ?? { columns: [] });
  if (!parsed.success) {
    return { columns: [] };
  }
  const cols: SpreadsheetColumn[] = parsed.data.columns;
  const colSeen = new Set<string>();
  const dedupedCols: SpreadsheetColumn[] = cols.filter((c) => {
    if (!c.field) return false;
    const key = `${c.model ?? ""}:${c.field}`;
    if (colSeen.has(key)) return false;
    colSeen.add(key);
    return true;
  });
  return {
    columns: dedupedCols,
  };
};

type BlockConfigContainerLike = {
  type?: string;
  config?: {
    spreadsheet?: SpreadsheetConfig | null;
    kanban?: unknown;
    form?: unknown;
    link?: unknown;
    calendar?: unknown;
  } | null;
};

export const getSpreadsheetConfigForBlock = (
  block: GetPageResponse["blocks"][number],
): SpreadsheetConfig => {
  const rawContainer = block.config as BlockConfigContainerLike | undefined;
  const rawSpreadsheet = rawContainer?.config?.spreadsheet ?? null;
  return sanitizeSpreadsheetConfig(rawSpreadsheet);
};

// Compute include[] for getInstances: handles of ONE_TO_ONE/MANY_TO_ONE relationship
// fields on the block's own model that are present as columns in the spreadsheet config.
export const computeRelationshipIncludesForBlock = (
  block: GetPageResponse["blocks"][number],
  pageData?: GetPageResponse,
): string[] => {
  if (!pageData) return [];
  const config = getSpreadsheetConfigForBlock(block);

  const handles = new Set<string>();
  for (const col of config.columns) {
    if (!col.field) continue;
    // Consider columns whose field handle exists on the block's model with eligible relationship cardinality
    const field = (pageData.fields || []).find(
      (f: {
        modelId: string;
        handle: string;
        relationshipLink?: { cardinality?: string } | null;
      }) =>
        f.modelId === (block as { modelId: string }).modelId &&
        f.handle === col.field,
    );
    const card = field?.relationshipLink?.cardinality;
    if (card === "ONE_TO_ONE" || card === "MANY_TO_ONE") {
      handles.add(col.field);
    }
  }
  return Array.from(handles.values());
};

// Cache columns per block to avoid remounting cell components when transient state updates
const spreadsheetColumnsCache: Map<string, SpreadsheetColumnsCacheEntry> =
  new Map();

const computeColumnsSignature = (
  block: GetPageResponse["blocks"][number],
  pageData?: GetPageResponse,
): string => {
  const cfg = getSpreadsheetConfigForBlock(block);
  const parts: string[] = [];
  parts.push(`model:${String(block.modelId)}`);
  for (const c of cfg.columns) {
    parts.push(
      `${String(c.model ?? "")}:${String(c.field ?? "")}:${String(
        c.width ?? "",
      )}`,
    );
  }
  const numFields =
    pageData?.fields?.filter(
      (f: { modelId: string }) => f.modelId === block.modelId,
    ).length || 0;
  parts.push(`fields:${numFields}`);
  return parts.join("|");
};

// Helper function to generate appropriate component for each field type
const generateComponentForField = (
  field: {
    system: boolean;
    type: DataType;
    definition?: {
      type?: string;
      options?: Array<{ value: string; label: string }>;
      limitChoices?: number | null;
      normalizations?: Array<{ handle: string }>;
    } | null;
    appearance?: {
      leftIcon?: string | null;
      rightIcon?: string | null;
      placeholder?: string | null;
      helpText?: string | null;
    } | null;
    label?: string;
    model?: { handlePlural?: string } | null;
    relationshipLink?: {
      id: string;
      cardinality: string;
      targetModelId: string;
      sourceModelId: string;
    } | null;
  },
  params: Params,
  accessorPath: string,
  pageData?: GetPageResponse,
  blockIdParam?: string,
  blockWorkingState?: ListWorkingState,
): ColumnConfig<RowData>["component"] => {
  const fieldType = field?.type || DataType.STRING;
  if (field.type === DataType.RELATION) {
    return createRelationshipCell(
      field,
      params,
      accessorPath,
      pageData,
      blockIdParam,
      (blockWorkingState ?? undefined) as
        | {
            rows?: Record<
              string,
              {
                fields?: Record<
                  string,
                  {
                    options?: {
                      value: string;
                      label: string;
                      avatar?: {
                        seed?: string | undefined;
                        url?: string | undefined;
                      };
                    }[];
                  }
                >;
              }
            >;
          }
        | undefined,
    ) as unknown as ColumnConfig<RowData>["component"];
  }
  if (field.definition?.options && field.definition.options.length > 0) {
    if (field.definition.limitChoices == 1) {
      return createSelectCell(field, params, accessorPath, pageData);
    }
    return createMultiSelectCell(field, params, accessorPath, pageData);
  }
  switch (fieldType) {
    case DataType.STRING:
    case DataType.LONG_STRING:
      if (isUrlField(field)) {
        return createUrlCell(params, accessorPath, pageData);
      }
      return createInputCell(
        {
          type: fieldType,
          definition: field.definition,
          appearance: field.appearance,
          label: field.label,
        },
        params,
        accessorPath,
        pageData,
      );
    case DataType.NUMBER:
      return createInputCell(
        {
          type: fieldType,
          definition: field.definition,
          appearance: field.appearance,
          label: field.label,
        },
        params,
        accessorPath,
        pageData,
      );
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.TIME:
      return createDatePickerCell(
        fieldType,
        field.system,
        params,
        accessorPath,
        pageData,
      );
    case DataType.BOOLEAN:
      return createBooleanCell(accessorPath);
    case DataType.FILE:
    case DataType.IMAGE:
      return createInputCell(
        {
          type: fieldType,
          definition: field.definition,
          appearance: field.appearance,
          label: field.label,
        },
        params,
        accessorPath,
        pageData,
      );
    case DataType.OBJECT:
      return createJsonCell(
        { label: field.label },
        params,
        accessorPath,
        pageData,
      );
    default:
      return createInputCell(
        {
          type: fieldType,
          definition: field.definition,
          appearance: field.appearance,
          label: field.label,
        },
        params,
        accessorPath,
        pageData,
      );
  }
};

// Helper function to generate spreadsheet columns from block config and model fields
export const generateSpreadsheetColumns = ({
  block,
  params,
  pageData,
  blockWorkingState,
}: GenerateColumnsArgs): ColumnConfig<RowData>[] => {
  const signature = computeColumnsSignature(block, pageData);
  const cached = spreadsheetColumnsCache.get(block.id);
  if (cached && cached.signature === signature) {
    // Return a fresh array and ensure no stray active_workflows column leaked into cache
    return cached.columns.filter((c) => c.id !== "active_workflows");
  }
  const config = getSpreadsheetConfigForBlock(block);
  const columns: ColumnConfig<RowData>[] = [];

  // get the page model and the block model
  const pageModel = pageData?.models?.find(
    (m: ModelLite) => m.id === pageData.modelId,
  );
  const blockModel = pageData?.models?.find(
    (m: ModelLite) => m.id === block.modelId,
  );

  // Add ID column first
  columns.push({
    id: "id",
    accessorKey: "id",
    header: "id",
    sticky: true,
    columnDataType: "id",
    component: {
      component: "CheckboxLabel",
      generateProps: (row: RowData, _index: number) => {
        const meta =
          (row._meta as
            | { image: { url?: string; seed?: string } | null; label: string }
            | undefined) || undefined;
        const label = meta?.label || row.id;
        const image = meta?.image || null;
        const avatar = image
          ? "url" in image && image.url
            ? { url: image.url }
            : "seed" in image && image.seed
              ? { seed: image.seed }
              : undefined
          : undefined;
        return {
          label,
          fallbackLabel: "—",
          showAvatar: pageModel?.imageEnabled,
          avatar,
          useRowSelection: true,
          href: buildInstanceHrefFromPageData(pageData, {
            subdomain: params.subdomain,
            instanceId: String(row.id),
            model: blockModel,
          }),
        };
      },
    },
  });

  // Get fields for this block and any related models that might be in columns
  const blockFields =
    pageData?.fields?.filter(
      (f: { modelId: string }) => f.modelId === block.modelId,
    ) || [];

  // Also get fields from related models that might be referenced in columns
  const relatedModelIds = new Set<string>();
  for (const columnConfig of config.columns) {
    if (!columnConfig.model) continue;
    const relatedModel = resolveSpreadsheetColumnModel(
      columnConfig.model,
      pageData,
    );
    if (relatedModel && relatedModel.id !== block.modelId) {
      relatedModelIds.add(relatedModel.id);
    }
  }

  const relatedFields =
    pageData?.fields?.filter((f: { modelId: string }) =>
      relatedModelIds.has(f.modelId),
    ) || [];
  const allFields = [...blockFields, ...relatedFields];

  // Generate columns from block config if available
  for (const columnConfig of config.columns) {
    const fieldHandle = columnConfig.field;
    if (!fieldHandle) continue;

    const columnModel = resolveSpreadsheetColumnModel(
      columnConfig.model,
      pageData,
    );

    const field = resolveSpreadsheetColumnField({
      fieldHandle,
      columnModel,
      blockModelId: block.modelId,
      fields: allFields,
    });

    if (!field) continue;

    const accessorPath = resolveAccessorPath(
      block,
      fieldHandle,
      columnConfig.model,
      pageData,
    );
    const column: ColumnConfigWithSizing = {
      id: accessorPath,
      accessorKey: accessorPath,
      header: field.shortLabel || field.label || fieldHandle || "",
      headerIcon: field.section
        ? ({
            icon: field.section.icon,
            iconUrl: field.section.iconUrl,
            label: field.section.name,
            details: field.section.details,
          } as ColumnConfig<RowData>["headerIcon"])
        : undefined,
      // Channel addresses live on the block's own model, not on joined columns.
      channels: accessorPath === fieldHandle ? field.channels : undefined,
      columnDataType: resolveSpreadsheetColumnDataType(field),
      component: generateComponentForField(
        {
          type: field.relationshipLink
            ? DataType.RELATION
            : (field.definition?.type as DataType | undefined) ??
              (field.dataType as DataType | undefined) ??
              DataType.STRING,
          definition: field.definition,
          label: field.label,
          system: field.system,
          model: columnModel,
          relationshipLink: field.relationshipLink,
        },
        params,
        accessorPath,
        pageData,
        block.id,
        blockWorkingState ?? undefined,
      ),
      isEditable: isUrlField(field) ? false : field.permissions?.write,
    };

    if (typeof columnConfig.width !== "undefined") {
      const parsedMaxWidth = Number(columnConfig.width);
      if (Number.isFinite(parsedMaxWidth)) {
        column.maxWidth = parsedMaxWidth;
      }
    }

    columns.push(column);
  }

  // NOTE: active_workflows column is appended in generateSpreadsheetComponent

  const finalized = columns.filter((c) => c.id !== "active_workflows");
  spreadsheetColumnsCache.set(block.id, {
    signature,
    columns: finalized,
  });
  return finalized;
};
